/**
 * variant-cards.js — keeps the "selected card" highlight in sync the instant a
 * card is clicked.
 *
 * The cards reuse Horizon's <variant-picker>, which on change fetches a fresh
 * server render of the product section and morphs it in (a network round-trip that
 * is noticeably slow on a heavy product page). The server render is the source of
 * the `.is-selected` class, so on its own the previously-selected card stays
 * highlighted until the fetch returns — making the selection feel laggy and, for a
 * moment, showing two cards as selected.
 *
 * This delegated listener moves `.is-selected` to the just-checked card right away,
 * scoped to its own option group, so the highlight is immediate regardless of the
 * fetch (and regardless of :has() support). The later morph re-applies the same
 * class server-side, so the two stay consistent.
 */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.classList.contains('variant-cards__input')) return;

  const checkedCard = input.closest('.variant-cards__card');
  if (!checkedCard) return;

  // Scope to the option group so multi-option products keep one selection per group.
  const scope = input.closest('.variant-cards__group') || input.closest('.variant-cards');
  if (!scope) return;

  scope.querySelectorAll('.variant-cards__card').forEach((card) => {
    card.classList.toggle('is-selected', card === checkedCard);
  });
});

/**
 * Default selected card.
 *
 * When the "Default selected card" setting forces a card (e.g. "2 frascos"), the block renders
 * that card selected/checked, but the rest of the page (add-to-cart variant, URL) is still
 * server-rendered for the storefront default variant. The picker only syncs the page on a real
 * `change`, so here we fire one on load — exactly as if the shopper had clicked that card — which
 * drives Horizon's tested path to update the add-to-cart variant and the ?variant URL.
 *
 * Guards: only when the block asked for it (data-default-variant-id), only on a bare load (no
 * ?variant in the URL — an explicit deep link wins), and only once per picker.
 */
function applyDefaultVariant(picker) {
  const targetId = picker.getAttribute('data-default-variant-id');
  if (!targetId || picker.dataset.defaultApplied) return;
  if (new URLSearchParams(window.location.search).has('variant')) return;

  const radio = picker.querySelector(`.variant-cards__input[data-variant-id="${targetId}"]`);
  if (!radio) return;

  picker.dataset.defaultApplied = '1';
  if (!radio.checked) radio.checked = true;
  radio.dispatchEvent(new Event('change', { bubbles: true }));
}

function initDefaultVariants() {
  document
    .querySelectorAll('variant-picker[data-default-variant-id]')
    .forEach((picker) => {
      // Wait for the custom element to be upgraded so its change listener is attached.
      customElements.whenDefined('variant-picker').then(() => {
        requestAnimationFrame(() => applyDefaultVariant(picker));
      });
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDefaultVariants);
} else {
  initDefaultVariants();
}
